import { getIn, useFormikContext } from 'formik';
import { Form } from 'react-bootstrap';

import DefaultButton from '@components/Button/button';
import FormInput from '@components/FormInput/formInput';

import { ShareResultsFormValues } from './ShareResults.types';

// Dummy data for available documents
const availableDocuments = [
  'Single-gene, 151 Genes report',
  'Medication Response report',
  'Coronary Artery Disease iPRS report',
  'Clinical Summary Letter',
];

const ShareResultsForm = () => {
  const { values, errors, touched, handleChange, handleBlur, setFieldValue, isSubmitting, handleSubmit } =
    useFormikContext<ShareResultsFormValues>();

  return (
    <Form onSubmit={handleSubmit} noValidate>
      <div className='d-flex flex-column gap-3'>
        <h1 className='text-primary pb-2 fw-semibold mb-0'>Share Results</h1>

        {/* Summary Section */}
        <div className='bg-light-blue p-4 rounded-3 mb-4'>
          <h5 className='text-primary mb-3'>Summary of what&apos;s being shared</h5>
          <p className='mb-3'>
            All available documents will be consolidated into a single PDF that gets attached to the email. You
            currently have {availableDocuments.filter(doc => !doc.includes('Letter')).length} report(s) and{' '}
            {availableDocuments.filter(doc => doc.includes('Letter')).length} clinical summary letter(s) available.
            Details below:
          </p>
          <ul className='mb-0'>
            {availableDocuments.map((doc, index) => (
              <li key={index} className='mb-2'>
                {doc}
              </li>
            ))}
          </ul>
        </div>

        {/* Recipient Information Form */}
        <div className='mb-4'>
          <h5 className='text-primary mb-3'>Recipient information</h5>
          <div className='bg-light-blue py-4 px-4 rounded-3'>
            <div className='row mb-2'>
              <div className='col-12 col-md-6 mb-4 mb-md-3'>
                <FormInput
                  value={values.recipient_email}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  label='Email *'
                  width='100%'
                  dataTestId='recipient_email'
                  id='recipient_email'
                  name='recipient_email'
                  type='email'
                  required
                  error={getIn(touched, 'recipient_email') && getIn(errors, 'recipient_email')}
                  errorText={getIn(errors, 'recipient_email') as string}
                  phiSanitize
                />
              </div>
              <div className='col-12 col-md-6 mb-3 mb-md-0'>
                <FormInput
                  value={values.confirm_email}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  label='Confirm Email *'
                  width='100%'
                  dataTestId='confirm_email'
                  id='confirm_email'
                  name='confirm_email'
                  type='email'
                  required
                  error={getIn(touched, 'confirm_email') && getIn(errors, 'confirm_email')}
                  errorText={getIn(errors, 'confirm_email') as string}
                  phiSanitize
                />
              </div>
            </div>
            <div className='row'>
              <div className='col-12 col-md-6 mb-2'>
                <FormInput
                  value={values.recipient_name}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  label="Recipient's Name"
                  width='100%'
                  dataTestId='recipient_name'
                  id='recipient_name'
                  name='recipient_name'
                  placeholder='ex. John Doe'
                  error={getIn(touched, 'recipient_name') && getIn(errors, 'recipient_name')}
                  errorText={getIn(errors, 'recipient_name') as string}
                  phiSanitize
                />
              </div>
            </div>
          </div>
        </div>

        {/* Agree to Share Section */}
        <div className='mb-4'>
          <h5 className='text-primary mb-3'>Agree to share</h5>
          <div className='bg-light-blue p-4 rounded-3'>
            <p className='mb-3'>
              I understand that this will email all results and letters listed above to the email address I entered. I
              have confirmed the recipient information is correct, and I understand that I cannot undo this action once
              the email is sent.
            </p>
            <p className='mb-3'>
              I acknowledge the risk associated with sharing health information via email, as it may not be an encrypted
              and secure method. By choosing this option, I accept the privacy risk.
            </p>
            <Form.Check
              id='agree_to_share'
              className={`fs-sm ${values.agree_to_share ? 'checked' : ''}`}
              onChange={e => setFieldValue('agree_to_share', e.target.checked)}
              checked={values.agree_to_share}
              isInvalid={(touched.agree_to_share || false) && !!errors.agree_to_share}
              type='checkbox'
              label='I understand and agree to results sharing'
            />
            {touched.agree_to_share && errors.agree_to_share && (
              <p className='text-danger text-start mt-1 mb-0' style={{ fontSize: '10px' }}>
                {errors.agree_to_share}
              </p>
            )}
          </div>
        </div>

        {/* Share Button */}
        <div className='d-flex justify-content-end'>
          <DefaultButton
            type='submit'
            buttonLabel={isSubmitting ? 'Sharing…' : 'Share'}
            styleClass='primaryButtonStyling'
            dataTestId='shareResultsBtn'
            onClick={() => {}}
          />
        </div>
      </div>
    </Form>
  );
};

export default ShareResultsForm;
